import { zodResolver } from '@hookform/resolvers/zod';
import { CATEGORY_NAME_MAX_LENGTH, categoryNameSchema, type ProductCategory } from '@easyhelp/shared';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

const renameSchema = z.object({ name: categoryNameSchema });
type RenameInput = z.input<typeof renameSchema>;

export interface CategoryDialogProps {
  open: boolean;
  category: ProductCategory;
  error?: string | null;
  busy?: boolean;
  onSubmit: (name: string) => void;
  onClose: () => void;
}

/** Rinomina di una categoria: i ticket già aperti conservano il nome con cui sono nati. */
export function CategoryDialog({ open, category, error = null, busy = false, onSubmit, onClose }: CategoryDialogProps) {
  const { control, handleSubmit } = useForm<RenameInput>({
    resolver: zodResolver(renameSchema),
    defaultValues: { name: category.name },
  });

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      fullWidth
      maxWidth="xs"
      aria-labelledby="category-dialog-title"
    >
      <form onSubmit={handleSubmit((values) => onSubmit(values.name))} noValidate>
        <DialogTitle id="category-dialog-title">Rinomina «{category.name}»</DialogTitle>
        <DialogContent>
          <Stack spacing={4} sx={{ pt: 1 }}>
            <DialogContentText>
              Il nuovo nome compare nel form del prodotto dalle segnalazioni successive; i ticket già aperti non
              cambiano.
            </DialogContentText>
            <Controller
              name="name"
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label="Nome della categoria"
                  required
                  autoFocus
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message}
                  slotProps={{ htmlInput: { maxLength: CATEGORY_NAME_MAX_LENGTH } }}
                />
              )}
            />
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={busy}>
            Annulla
          </Button>
          <Button type="submit" variant="contained" disabled={busy}>
            Salva
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
